import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { Search, Filter, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';

const STATUS_LABELS = {
  unpublished: { label: 'Unpublished', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  open: { label: 'Open', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  closed: { label: 'Closed', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

function examStatus(exam) {
  if (exam.is_published !== 'published') return 'unpublished';
  return exam.submissions_open ? 'open' : 'closed';
}

function StatusBadge({ exam }) {
  const cfg = STATUS_LABELS[examStatus(exam)];
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

export default function AdminExamsPage() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    adminApi.listExams()
      .then(({ data }) => setExams(data))
      .catch(() => toast.error('Failed to load exams'))
      .finally(() => setLoading(false));
  }, []);

  const facultyOptions = useMemo(() => {
    const seen = new Map();
    exams.forEach((e) => seen.set(e.faculty_id, e.faculty_name));
    return Array.from(seen.entries());
  }, [exams]);

  const filteredExams = useMemo(() => {
    return exams.filter((e) => {
      if (search && !e.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (facultyFilter && e.faculty_id !== facultyFilter) return false;
      if (statusFilter && examStatus(e) !== statusFilter) return false;
      return true;
    });
  }, [exams, search, facultyFilter, statusFilter]);

  const clearFilters = () => {
    setSearch('');
    setFacultyFilter('');
    setStatusFilter('');
  };

  return (
    <DashboardLayout title="Exam Oversight">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            Exam Oversight
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            All exams across every faculty member — read-only.
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title..."
                className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={facultyFilter}
                onChange={(e) => setFacultyFilter(e.target.value)}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="faculty-filter"
              >
                <option value="">All Faculty</option>
                {facultyOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-9 pr-8 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors appearance-none"
                  data-testid="status-filter"
                >
                  <option value="">All Status</option>
                  <option value="unpublished">Unpublished</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              {(search || facultyFilter || statusFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  data-testid="clear-filters-btn"
                  title="Clear filters"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/6"></div>
                </div>
              ))}
            </div>
          ) : filteredExams.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
              <FileText size={28} className="text-slate-300 dark:text-slate-600" />
              No exams found.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Title</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Subject</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Faculty</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Status</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Assigned</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Submitted</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredExams.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/admin/exams/${e.id}`)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
                    data-testid={`exam-row-${e.id}`}
                  >
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">{e.title}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.subject ?? '—'}</td>
                    <td className="px-5 py-3">
                      <p className="text-slate-900 dark:text-white">{e.faculty_name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{e.faculty_email}</p>
                    </td>
                    <td className="px-5 py-3"><StatusBadge exam={e} /></td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.assignment_count}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.submission_count}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
